import { useFormikContext } from 'formik';
import { ReleaseIdCard } from 'front-office/view-models/release-id-card';
import { Box, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { Check } from '@mui/icons-material';
import { memo, useMemo } from 'react';
import { StepButton } from 'shell/components/shared/step-button';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { ButtonType, ModalType } from '../modal-print';

interface ReleaseIdCardButtonsProps {
  onCancel: () => void;
  setModalType: (type: ModalType) => void;
  pressedButtons: Array<ButtonType>;
}

const ReleaseIdCardButtonsComponent = ({
  onCancel,
  setModalType,
  pressedButtons,
}: ReleaseIdCardButtonsProps): React.ReactElement => {
  const formContext = useFormikContext<ReleaseIdCard>();

  // errors
  const errorOtherData = useMemo(() => {
    return Boolean(formContext.errors.otherData || false);
  }, [formContext.errors.otherData]);

  const errorDocument = useMemo(() => {
    return Boolean(formContext.errors.document || false);
  }, [formContext.errors.document]);

  const errorRegistryOffice = useMemo(() => {
    return Boolean(formContext.errors.registryOffice || false);
  }, [formContext.errors.registryOffice]);

  const onClickOtherData = useEventCallback(() => {
    formContext.setValues({
      ...formContext.values,
      isOtherData: true,
      isRegistryOffice: false,
      isResidence: false,
      isDocument: false,
    });
  });

  const onClickRegistryOffice = useEventCallback(() => {
    formContext.setValues({
      ...formContext.values,
      isOtherData: false,
      isRegistryOffice: true,
      isResidence: false,
      isDocument: false,
    });
  });

  const onClickResidence = useEventCallback(() => {
    formContext.setValues({
      ...formContext.values,
      isOtherData: false,
      isRegistryOffice: false,
      isResidence: true,
      isDocument: false,
    });
  });

  const onClickDocument = useEventCallback(() => {
    formContext.setValues({
      ...formContext.values,
      isOtherData: false,
      isRegistryOffice: false,
      isResidence: false,
      isDocument: true,
    });
  });

  const disabledPrintCard = useMemo(() => {
    let disabled = true;
    if (
      pressedButtons.find((el) => el === ButtonType.PrintTagQuestura) &&
      pressedButtons.find((el) => el === ButtonType.PrintTagComune)
    ) {
      disabled = false;
    }
    return disabled;
  }, [pressedButtons]);

  return (
    <StickyDiv>
      <div>
        <div className="pmi-pb-4">
          <StepButton
            active={formContext.values.isRegistryOffice}
            isButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={onClickRegistryOffice}
            style={{ margin: '0 0 16px 0' }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                Anagrafe &nbsp;
                <Check color={errorRegistryOffice ? 'error' : 'success'} />
              </span>
            </Box>
          </StepButton>
          <StepButton
            active={formContext.values.isResidence}
            isButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={onClickResidence}
            style={{ margin: '0 0 16px 0' }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ display: 'flex', justifyContent: 'center' }}>Residenza</span>
            </Box>
          </StepButton>
          <StepButton
            active={formContext.values.isOtherData}
            isButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={onClickOtherData}
            style={{ margin: '0 0 16px 0' }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                Altri dati &nbsp;
                <Check color={errorOtherData ? 'error' : 'success'} />
              </span>
            </Box>
          </StepButton>
          <StepButton
            active={formContext.values.isDocument}
            isButton
            variant="outlined"
            color="cdmBrandThird"
            fullWidth
            onClick={onClickDocument}
            style={{ margin: '0 0 16px 0' }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                Documenti &nbsp;
                <Check color={errorDocument ? 'error' : 'success'} />
              </span>
            </Box>
          </StepButton>
        </div>
        <div className="pmi-pb-2">
          <PmiButton
            variant="contained"
            color="cdmBrandThird"
            fullWidth
            disabled={formContext.submitCount > 0 && !formContext.isValid}
            type="submit"
            onClick={() => {
              setModalType(ModalType.Cartellino);
            }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Conferma Stampa Cartellino
            </Box>
          </PmiButton>
        </div>
        <div className="pmi-pb-2">
          <PmiButton
            variant="contained"
            color="cdmBrandThird"
            fullWidth
            disabled={disabledPrintCard}
            type="submit"
            onClick={() => {
              setModalType(ModalType.Cid);
            }}
          >
            <Box
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              Conferma Stampa Carta
            </Box>
          </PmiButton>
        </div>
      </div>
      <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={onCancel}>
        Annulla operazione
      </PmiButton>
    </StickyDiv>
  );
};

ReleaseIdCardButtonsComponent.displayName = 'Release id card form component';

const ReleaseIdCardButtons = memo(ReleaseIdCardButtonsComponent);

export default ReleaseIdCardButtons;
